import type { Member } from '@tsuyosa/types';

interface MemberCardProps {
  photo?: string | null;
  name: string;
  quantity: number;
  onSelect: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
}

function MemberCard({ photo, name, quantity, onSelect, onIncrement, onDecrement }: MemberCardProps) {
  const initials   = name.slice(0, 2).toUpperCase();
  const isSelected = quantity > 0;

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={isSelected ? undefined : onSelect}
        className="w-full"
        style={{ cursor: isSelected ? 'default' : 'pointer' }}
      >
        <div
          className="relative w-full overflow-hidden rounded-xl transition-all duration-150"
          style={{
            aspectRatio: '1 / 1',
            outline: isSelected ? '2px solid var(--pink)' : '2px solid transparent',
            outlineOffset: '2px',
            boxShadow: isSelected ? '0 0 12px var(--pink-glow)' : 'none',
          }}
        >
          {photo ? (
            <img src={photo} alt={name} className="h-full w-full object-cover object-top" />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-xl font-bold text-white/40"
              style={{ background: 'var(--surface-hi)' }}
            >
              {initials}
            </div>
          )}

          {isSelected && (
            <div
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
              style={{ background: 'var(--pink)' }}
            >
              {quantity}
            </div>
          )}
        </div>
      </button>

      <span
        className="max-w-full truncate text-center text-xs leading-tight transition-colors"
        style={{ color: isSelected ? 'white' : 'rgba(255,255,255,0.5)' }}
      >
        {name}
      </span>

      {isSelected && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onDecrement}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs text-white transition-colors hover:bg-white/5"
            style={{ borderColor: 'var(--border)' }}
          >
            −
          </button>
          <span className="w-4 text-center text-xs font-bold text-white">{quantity}</span>
          <button
            type="button"
            onClick={onIncrement}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs text-white transition-colors hover:bg-white/5"
            style={{ borderColor: 'var(--border)' }}
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}

interface Props {
  members: Member[];
  value: Record<string, number>;
  onChange: (value: Record<string, number>) => void;
}

export function MemberGrid({ members, value, onChange }: Props) {
  function handleSelect(id: string) {
    onChange({ ...value, [id]: 1 });
  }

  function handleIncrement(id: string) {
    onChange({ ...value, [id]: (value[id] ?? 0) + 1 });
  }

  function handleDecrement(id: string) {
    const next = { ...value };
    if ((next[id] ?? 0) <= 1) {
      delete next[id];
    } else {
      next[id] = (next[id] ?? 0) - 1;
    }
    onChange(next);
  }

  return (
    <div>
      <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-white/40">Member</p>
      <div className="grid grid-cols-3 gap-3">
        {members.map(m => (
          <MemberCard
            key={m.id}
            photo={m.profile_photo}
            name={m.nickname}
            quantity={value[m.id] ?? 0}
            onSelect={() => handleSelect(m.id)}
            onIncrement={() => handleIncrement(m.id)}
            onDecrement={() => handleDecrement(m.id)}
          />
        ))}
      </div>
    </div>
  );
}
